/**
 * color-tile-carousel.js  v5
 * 基于原版表单操作架构，修复 GID 编码匹配问题
 * v5: 桌面端也显示翻页箭头（按是否溢出判断，而非按屏幕宽度）
 */

const FILTER_NAME = 'filter.v.t.shopify.color-pattern';

class ColorTileCarousel extends HTMLElement {
  constructor() {
    super();
    this._isTogglingSelf = false;
    this._lastUrl = '';
    this._urlPollInterval = null;
    this._loadingTimer = null;

    this._onClick       = this._onClick.bind(this);
    this._onPopState    = this._onPopState.bind(this);
    this._onDocChange   = this._onDocChange.bind(this);
    this._checkUrlChange = this._checkUrlChange.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
    window.addEventListener('popstate', this._onPopState);
    document.addEventListener('change', this._onDocChange, true);

    this._lastUrl = window.location.href;
    this._urlPollInterval = setInterval(this._checkUrlChange, 250);

    this._syncFromUrl();

    // Scroll arrows
    this._bindArrows();
    this._updateArrows();
    this.querySelector('[data-ctc-track]')?.addEventListener(
      'scroll', () => this._updateArrows(), { passive: true }
    );
    window.addEventListener('resize', () => this._updateArrows(), { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopState);
    document.removeEventListener('change', this._onDocChange, true);
    if (this._urlPollInterval) clearInterval(this._urlPollInterval);
  }

  // ============================================================
  // Form lookup
  // ============================================================

  getForm() {
    const candidates = document.querySelectorAll('form[id^="FacetFiltersForm--"]');
    for (const form of candidates) {
      if (form.querySelector(`input[name="${FILTER_NAME}"]`)) return form;
    }
    return candidates[0] || null;
  }

  /**
   * Find checkbox by value — try exact match first, then decoded comparison.
   * Checkbox values in the DOM may be raw GIDs; URL params may be encoded.
   */
  findCheckbox(value) {
    const form = this.getForm();
    if (!form) return null;

    const decoded = decodeURIComponent(value);

    // 1. Exact match
    const exact = form.querySelector(
      `input[type="checkbox"][name="${FILTER_NAME}"][value="${CSS.escape(value)}"]`
    );
    if (exact) return exact;

    // 2. Decoded match — iterate when encoding differs
    return Array.from(
      form.querySelectorAll(`input[type="checkbox"][name="${FILTER_NAME}"]`)
    ).find(cb => decodeURIComponent(cb.value) === decoded) || null;
  }

  getAllColorCheckboxes() {
    return Array.from(
      document.querySelectorAll(
        `form[id^="FacetFiltersForm--"] input[type="checkbox"][name="${FILTER_NAME}"]`
      )
    );
  }

  // ============================================================
  // Click handlers
  // ============================================================

  _onClick(e) {
    const tile = e.target.closest('[data-ctc-action]');
    if (!tile) return;
    e.preventDefault();
    e.stopPropagation();

    switch (tile.dataset.ctcAction) {
      case 'toggle-family': this._toggleFamily(tile); break;
      case 'clear-all':     this._clearAll();          break;
    }
  }

  _toggleFamily(tile) {
    const gids = (tile.dataset.ctcGids || '')
      .split(',').map(g => g.trim()).filter(Boolean);

    if (gids.length === 0) {
      console.warn('[ctc] Tile has no GIDs:', tile);
      return;
    }

    const checkboxes = gids.map(g => this.findCheckbox(g)).filter(Boolean);

    if (checkboxes.length === 0) {
      console.warn('[ctc] No matching checkboxes for GIDs:', gids);
      return;
    }

    const anyChecked = checkboxes.some(cb => cb.checked);
    const target = !anyChecked;

    tile.classList.toggle('is-active', target);
    tile.classList.add('is-loading');
    this._showLoading();

    this._isTogglingSelf = true;
    checkboxes.forEach(cb => { cb.checked = target; });
    this._dispatchChange(checkboxes[checkboxes.length - 1]);
    setTimeout(() => { this._isTogglingSelf = false; }, 0);
  }

  _clearAll() {
    const checked = this.getAllColorCheckboxes().filter(cb => cb.checked);
    if (checked.length === 0) return;

    this.querySelectorAll('.ctc-tile.is-active').forEach(t => t.classList.remove('is-active'));
    const allTile = this.querySelector('.ctc-tile--all');
    if (allTile) allTile.classList.add('is-active', 'is-loading');

    this._showLoading();

    this._isTogglingSelf = true;
    checked.forEach(cb => { cb.checked = false; });
    this._dispatchChange(checked[checked.length - 1]);
    setTimeout(() => { this._isTogglingSelf = false; }, 0);
  }

  // ============================================================
  // State sync
  // ============================================================

  _onPopState() { this._syncFromUrl(); }

  _onDocChange(e) {
    if (this._isTogglingSelf) return;
    const t = e.target;
    if (!t || t.tagName !== 'INPUT' || t.type !== 'checkbox') return;
    if (t.name !== FILTER_NAME) return;
    this._syncFromForm();
    this._showLoading();
  }

  _checkUrlChange() {
    const current = window.location.href;
    if (current !== this._lastUrl) {
      this._lastUrl = current;
      this._syncFromUrl();
      this._hideLoading();
    }
  }

  _syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    // Decode all values so they match raw GIDs in data-ctc-gids
    const activeValues = new Set(
      params.getAll(FILTER_NAME).map(v => decodeURIComponent(v))
    );
    this._applyActiveSet(activeValues);
  }

  _syncFromForm() {
    const form = this.getForm();
    if (!form) { this._syncFromUrl(); return; }
    const checked = new Set(
      Array.from(
        form.querySelectorAll(`input[type="checkbox"][name="${FILTER_NAME}"]:checked`)
      ).map(cb => decodeURIComponent(cb.value))   // ← decode here too
    );
    this._applyActiveSet(checked);
  }

  /**
   * Update tile active states.
   * Both activeValues and tile GIDs are decoded before comparison.
   */
  _applyActiveSet(activeValues) {
    let anyTileActive = false;

    this.querySelectorAll('.ctc-tile[data-ctc-gids]').forEach(tile => {
      const gids = (tile.dataset.ctcGids || '')
        .split(',')
        .map(g => decodeURIComponent(g.trim()))   // ← decode tile GIDs
        .filter(Boolean);

      const hasAny = gids.some(g => activeValues.has(g));
      tile.classList.toggle('is-active', hasAny);
      if (hasAny) anyTileActive = true;
    });

    const allTile = this.querySelector('.ctc-tile--all');
    if (allTile) {
      allTile.classList.toggle('is-active', activeValues.size === 0);
    }
  }

  // ============================================================
  // Loading state
  // ============================================================

  _showLoading() {
    document.documentElement.classList.add('cfm-products-loading');
    clearTimeout(this._loadingTimer);
    this._loadingTimer = setTimeout(() => this._hideLoading(), 6000);
  }

  _hideLoading() {
    document.documentElement.classList.remove('cfm-products-loading');
    clearTimeout(this._loadingTimer);
    this.querySelectorAll('.ctc-tile.is-loading').forEach(t => t.classList.remove('is-loading'));
  }

  // ============================================================
  // Trigger theme AJAX
  // ============================================================

  _dispatchChange(input) {
    if (!input) return;
    input.dispatchEvent(new Event('input',  { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ============================================================
  // Scroll arrows (desktop + mobile)
  // ============================================================

  _bindArrows() {
    this.querySelectorAll('[data-ctc-arrow]').forEach(arrow => {
      arrow.addEventListener('click', () => {
        const track = this.querySelector('[data-ctc-track]');
        if (!track) return;
        const dir   = arrow.dataset.ctcArrow;
        const first = track.querySelector('.ctc-tile');
        const gap   = parseInt(getComputedStyle(track).gap || '6', 10);
        const step  = first ? first.offsetWidth + gap : 160;
        track.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
      });
    });
  }

  _updateArrows() {
    const arrows = this.querySelectorAll('[data-ctc-arrow]');
    const track  = this.querySelector('[data-ctc-track]');
    if (!track) return;

    // 没有溢出（内容放得下）就两个箭头都隐藏 —— 桌面/移动统一逻辑
    const hasOverflow = track.scrollWidth - track.clientWidth > 4;
    if (!hasOverflow) {
      arrows.forEach(a => a.classList.add('is-hidden'));
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const atStart = scrollLeft <= 4;
    const atEnd   = scrollLeft + clientWidth >= scrollWidth - 4;
    arrows.forEach(arrow => {
      const isLeft = arrow.dataset.ctcArrow === 'left';
      arrow.classList.toggle('is-hidden', isLeft ? atStart : atEnd);
    });
  }
}

if (!customElements.get('color-tile-carousel')) {
  customElements.define('color-tile-carousel', ColorTileCarousel);
}
